import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { CircleSpinner } from 'react-spinners-kit';
import { useButton, useDispatch, useI18n, useLogger, usePrinterApi, useSelector } from '~Hooks';
import gql from 'graphql-tag';
import { useMutation, useQuery } from '@apollo/client';
import {
  AddPrinter,
  AddPrinterVariables,
  GetPrinters,
  GetPrintersVariables,
  GetStations,
} from './__queries__';
import Heading from '~ComponentsImproved/Typography/Heading';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { Box, GreenButton } from '~Styles';
import AddedPrinter from './Components/AddedPrinter';
import { getPrinterID } from './utils';
import Icon from '~ComponentsImproved/Icon';
import ScanIcon from '~Assets/icons/sensors.svg';
import PrinterToAdd from './Components/PrinterToAdd';
import VirtualTestPrinterForm from './Components/VirtualTestPrinter';
import * as actions from '~actions';
import NotFoundPrinterTroubleshooting from './Troubleshooting/NotFound';
import { handyBreakpoint } from '~Styles/breakpoints';
import CloudPrintHistory from './Components/CloudPrintHistory';

const Wrapper = styled.div`
  width: 100%;
  padding: 24px 32px;
  box-sizing: border-box;

  h1 {
    font-size: 20px;
  }

  ${handyBreakpoint} {
    padding: 0;
  }
`;

const TabBar = styled.div`
  display: flex;
  gap: 0;
  margin-bottom: 24px;
  border-bottom: 2px solid var(--colorBlueGray900);
`;

const Tab = styled.button<{ active: boolean }>`
  padding: 12px 24px;
  background: none;
  border: none;
  border-bottom: 2px solid ${({ active }) => (active ? 'var(--colorWhite)' : 'transparent')};
  margin-bottom: -2px;
  color: ${({ active }) => (active ? 'var(--colorWhite)' : 'var(--colorBlueGray400)')};
  font-size: 16px;
  font-weight: ${({ active }) => (active ? 600 : 400)};
  cursor: pointer;
  transition: all 0.15s ease-out;

  &:active {
    transform: scale(0.96);
  }
`;

const AddedPrintersWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(550px, 1fr));
  gap: 24px;
  width: 100%;
  box-sizing: border-box;
  margin-top: 24px;

  ${handyBreakpoint} {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
`;

const ScanButton = styled(GreenButton)`
  display: flex;
  gap: 16px;
  align-items: center;
  align-self: center;
  width: 300px;
  max-width: 100%;
  height: 82px;
  font-size: 16px;
`;

const ScanWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 24px;

  ${handyBreakpoint} {
    flex-direction: column;

    & > button {
      align-self: flex-start;
      width: 100%;
    }

    .SVG-icon {
      display: none;
    }

    h1 {
      font-size: 20;
    }
    p {
      font-size: 16px;
    }
  }
`;

const TroubleShootWrapper = styled(Box)`
  border: 2px solid var(--colorBlueGray900);
  border-radius: 4px;
  padding: 8px;
  display: flex;
  align-items: center;
  align-self: center;
  justify-content: space-around;
  margin-top: 8px;
  width: 100%;

  ${handyBreakpoint} {
    display: none;
  }
`;

const STATIONS_QUERY = gql`
  query GetStations {
    user {
      id
      company {
        id
        stations {
          id
          name
        }
      }
    }
  }
`;

const PRINTER_QUERY = gql`
  query GetPrinters($venueId: ID!) {
    venue: Venue(id: $venueId) {
      id
      printers {
        id
        name
        stations
        type
      }
      cloudPrinters {
        id
        status
        statusCode
        lastSeen
        lastPrinted
      }
    }
  }
`;

const ADD_VENUE_PRINTER = gql`
  mutation AddPrinter(
    $venueId: String!
    $type: String!
    $id: String!
    $stations: [String!]!
    $name: String!
  ) {
    addPrinter(venueId: $venueId, id: $id, type: $type, stations: $stations, name: $name) {
      success
      message
    }
  }
`;

/**
 *
 * PRINTERS
 *
 * This page does a couple things:
 * 1. Lists active printers
 * 2. Lists available printers after user clicks "start scan"
 * 3. Lists printers stored in backend
 *
 * An "Active printer" is simply a printer that has been added to the array of
 * printers in permanent storage. It will be used for printing when relevant.
 *
 * Printers stored in backend are used for easy setup and information sharing in complex environments.
 * For example if there's 10 printers and 10 iPads, it can be a lot of work and a lot of room for
 * error to keep track of which IP corresponds to which printer etc.
 *
 */
type PrinterTab = 'settings' | 'history';

const Printers = () => {
  const { i18n } = useI18n();
  const [, , venueId] = location.hash.split('/');
  const printerApi = usePrinterApi();
  const [activeTab, setActiveTab] = useState<PrinterTab>('settings');

  const logger = useLogger('settings-v2');

  const printers = useSelector((state) => state.printers);

  const isScanning = useSelector((state) => state.POSPrinterScanActive);
  const availPrinters = useSelector((state) => state.availablePrinters).filter(
    (p) => !printers.some((printer) => printer.id === p.id),
  );

  const [troubleshoot, setTroubleshoot] = useState(false);

  const dispatch = useDispatch();

  useEffect(() => {
    logger.info('Available printers', { availPrinters });
  }, [availPrinters.length]);

  /**
   * Get printers from backend
   */
  const printerQuery = useQuery<GetPrinters, GetPrintersVariables>(PRINTER_QUERY, {
    variables: {
      venueId,
    },
    // Gently poll for remote changes
    pollInterval: 10000,
  });

  const stationsQuery = useQuery<GetStations>(STATIONS_QUERY);

  /**
   * Mutation that adds (and updates!) printers in backend.
   * Technically we "add" a printer when its updated, but since printers are stored on a
   * ID-basis it will simply overwrite the previous data
   */
  const [addPrinterToRemote] = useMutation<AddPrinter, AddPrinterVariables>(ADD_VENUE_PRINTER, {
    refetchQueries: ['GetPrinters'],
  });

  const venuePrinters = printerQuery.data?.venue?.printers ?? [];
  const cloudPrinters = printerQuery.data?.venue?.cloudPrinters ?? [];

  useEffect(() => {
    logger.debug('Venue printers', { venuePrinters });
  }, [venuePrinters]);

  async function updateVenuePrinter(
    printerId: string,
    name: string,
    stations: string[],
    type: string,
  ) {
    addPrinterToRemote({
      variables: {
        venueId,
        id: getPrinterID(printerId),
        name,
        type,
        stations,
      },
    });
    logger.info('Updating venue printer', { fields: { printerId, name, stations, type } });
    dispatch(actions.addToast('Printer updated', true));
  }

  function startPrinterScan() {
    logger.info('Starting printer scan');
    window.postMessageReact('available-printers');
    //Setting a small timeout so that the troubleshoot window doesnt pop in quickly before the scan starts
    setTimeout(() => {
      setTroubleshoot(true);
    }, 100);
  }

  const scanButton = useButton({
    onClick: startPrinterScan,
  });

  /**
   * Merges printer data with data we have stored in backend from before.
   * This is so we can annotate the found printers with their name
   * for easier onboarding and discovery
   */
  const availablePrinters = availPrinters.map((ap) => {
    const remote = venuePrinters.find((vp) => vp.id === ap.id);

    const name = remote?.name || ap.id;

    return {
      id: ap.id,
      // Name defaults to the id of the printer when saved.
      // For logic further below, it's better to keep name as null in that case
      name: name !== ap.id ? name : null,

      type: ap.type,
      model: ap.model,
      stations: remote?.stations ?? [],
    };
  });

  /* Filter out cloudPrinters that have already been added */
  const filteredCloudPrinters = cloudPrinters.filter(
    (cloudPrinter) => !printers.some((printer) => printer.id === cloudPrinter.id),
  );

  const hasCloudPrinters = cloudPrinters.length > 0 || printers.some((p) => p.model === 'StarCloudPrinter');

  // Show the print history tab if this venue has cloud printers
  if (activeTab === 'history') {
    return (
      <Wrapper>
        {hasCloudPrinters && (
          <TabBar>
            <Tab active={false} onClick={() => setActiveTab('settings')}>
              {i18n('Settings.Printers')}
            </Tab>
            <Tab active={true} onClick={() => setActiveTab('history')}>
              Print History
            </Tab>
          </TabBar>
        )}
        <CloudPrintHistory />
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      {hasCloudPrinters && (
        <TabBar>
          <Tab active={true} onClick={() => setActiveTab('settings')}>
            {i18n('Settings.Printers')}
          </Tab>
          <Tab active={false} onClick={() => setActiveTab('history')}>
            Print History
          </Tab>
        </TabBar>
      )}

      {!window.hasNativeWrapper && window.__DEV__ && <VirtualTestPrinterForm />}

      {printers.length > 0 && (
        <Box marginBottom="3xl">
          <Heading level={1} color="#fff">
            {i18n('Settings.AddedPrinters')}
          </Heading>
          {printers.length > 0 && (
            <AddedPrintersWrapper>
              {printers.map((printer) => {
                const venuePrinter = venuePrinters.find(
                  (vp) => getPrinterID(vp.id) === printer.id,
                );
                const cloudPrinter = cloudPrinters.find((cp) => cp.id === printer.id);

                return (
                  <AddedPrinter
                    key={printer.id}
                    printer={printer}
                    stations={stationsQuery.data?.user.company.stations ?? []}
                    venuePrinter={venuePrinter}
                    updateVenuePrinter={updateVenuePrinter}
                    cloudPrinter={cloudPrinter}
                  />
                );
              })}
            </AddedPrintersWrapper>
          )}
        </Box>
      )}

      <Box marginBottom="xl" marginTop="large" display="flex" flexDirection="column">
        <ScanWrapper>
          <div>
            <Heading level={1} color="#fff">
              {i18n('Settings.AddPrinters')}
            </Heading>
            <Paragraph level={2} color="var(--colorBlueGray200)" top={16}>
              {[...availablePrinters, ...filteredCloudPrinters].length > 0
                ? i18n('Settings.AddPrinters.Desc')
                : i18n('Settings.AddPrinters.ScanInfo')}
            </Paragraph>
          </div>
          <ScanButton {...scanButton}>
            {isScanning ? (
              <CircleSpinner size={24} color="#fff" />
            ) : (
              <>
                {i18n('Settings.ScanForPrinters')}
                <Icon src={ScanIcon} height="40px" width="40px" />
              </>
            )}
          </ScanButton>
        </ScanWrapper>
        {availablePrinters.map((aPrinter) => {
          if (!aPrinter.id) {
            logger.warn('Skipping printer with invalid id', { printer: aPrinter });
            return null;
          }

          const venuePrinter = venuePrinters.find((vp) => vp.id.split('|')[0] === aPrinter.id);

          return (
            <PrinterToAdd
              key={aPrinter.id}
              id={aPrinter.id}
              name={venuePrinter?.name ?? aPrinter.name ?? undefined}
              model={aPrinter.model}
              type={aPrinter.type}
              venuePrinter={venuePrinter}
              updateVenuePrinter={updateVenuePrinter}
            />
          );
        })}
        {filteredCloudPrinters.length > 0 && (
          <>
            {cloudPrinters.map((cp) => {
              const venuePrinter = venuePrinters.find((vp) => vp.id.split('|')[0] === cp.id);

              if (!cp.id) {
                logger.warn('Skipping cloud printer with invalid id', { printer: cp });
                return null;
              }

              return (
                <PrinterToAdd
                  key={cp.id}
                  id={cp.id}
                  name={printerApi.getNameFromId(cp.id) ?? ''}
                  model="StarCloudPrinter"
                  type="StarIO"
                  venuePrinter={venuePrinter}
                  updateVenuePrinter={updateVenuePrinter}
                />
              );
            })}
          </>
        )}
        {!isScanning && troubleshoot && (
          <TroubleShootWrapper>
            <Paragraph level={2} color="#fff" fontWeight={600}>
              {i18n('Settings.Printers.TroubleInfo')}
            </Paragraph>
            <NotFoundPrinterTroubleshooting startPrinterScan={startPrinterScan} />
          </TroubleShootWrapper>
        )}
      </Box>
    </Wrapper>
  );
};

export default Printers;
